import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async'; // Fixed import
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import HeroSection from './components/HeroSection';
import ServiceCategories from './components/ServiceCategories';
import ServiceCard from './components/ServiceCard';
import ComparisonMatrix from './components/ComparisonMatrix';
import StatsSection from './components/StatsSection';
import FAQSection from './components/FAQSection';
import CTASection from './components/CTASection';
import { Service, ServiceCategory, ComparisonFeature, Stat, FAQ } from './types';

const ServicesOverview = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Structured Data for Google (Service List)
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": [
      {
        "@type": "Service",
        "position": 1,
        "name": "AI & Chatbot Automation",
        "description": "Intelligent conversational AI for customer support and lead gen."
      },
      {
        "@type": "Service",
        "position": 2,
        "name": "Workflow Automation",
        "description": "n8n and Make.com workflows to eliminate manual data entry."
      },
      {
        "@type": "Service",
        "position": 3,
        "name": "SEO Services",
        "description": "Rank higher on Google and turn organic search into your most reliable source of new business."
      },
      {
        "@type": "Service",
        "position": 4,
        "name": "Local SEO",
        "description": "Own the map pack and 'near me' searches so nearby customers find you first."
      },
      {
        "@type": "Service",
        "position": 5,
        "name": "Social Media Marketing",
        "description": "Content, community, and campaigns that turn followers into enquiries and customers."
      },
      {
        "@type": "Service",
        "position": 6,
        "name": "Google Ads & PPC",
        "description": "Search and shopping ads that put you at the top of Google from day one, with every rupee tracked."
      },
      {
        "@type": "Service",
        "position": 7,
        "name": "Meta Ads",
        "description": "Facebook and Instagram ads engineered for leads, sales, and retargeting, not just likes."
      },
      {
        "@type": "Service",
        "position": 8,
        "name": "Content Marketing",
        "description": "Blogs, videos, and guides that answer buyer questions, build trust, and keep ranking for years."
      },
      {
        "@type": "Service",
        "position": 9,
        "name": "Email Marketing",
        "description": "Campaigns and automations that turn your list into repeat revenue, without spamming anyone."
      },
      {
        "@type": "Service",
        "position": 10,
        "name": "Analytics & CRO",
        "description": "Dashboards, experiments, and fixes that turn more of your existing traffic into customers."
      }
    ]
  };

  const serviceCategories: ServiceCategory[] = [
    { id: 'all', name: 'All Solutions', description: 'Complete catalog', icon: 'Grid' },
    { id: 'ai-automation', name: 'AI & Chatbots', description: 'Intelligent agents', icon: 'Bot' },
    { id: 'workflow', name: 'Workflows', description: 'Ops automation', icon: 'Workflow' },
    { id: 'communication', name: 'Growth', description: 'Marketing auto', icon: 'TrendingUp' },
    { id: 'digital-marketing', name: 'Digital Marketing', description: 'Growth services', icon: 'TrendingUp' }
  ];

  const services: Service[] = [
    {
      id: '1',
      title: 'AI Customer Support Agents',
      description: 'Deploy intelligent conversational AI that handles customer inquiries 24/7, qualifies leads automatically, and provides instant support.',
      icon: 'Bot',
      category: 'ai-automation',
      features: ['GPT-4o Integration', 'Multi-channel (Web/WhatsApp)', 'Lead Qualification', 'CRM Sync', 'Sentiment Analysis'],
      benefits: ['Reduce support costs by 70%', '0 second response time', '24/7 Availability'],
      useCases: ['Customer Support', 'Lead Gen', 'Booking Appointments'],
      tools: [
        { name: 'OpenAI', logo: "https://upload.wikimedia.org/wikipedia/commons/4/4d/OpenAI_Logo.svg", alt: 'OpenAI' },
        { name: 'Voiceflow', logo: "https://yt3.googleusercontent.com/ytc/AIdro_k6aF6_j4vTD9U_aK_FkPScUjV8_wOQ6Z55_P-M=s900-c-k-c0x00ffffff-no-rj", alt: 'Voiceflow' }
      ],
      pricing: { starter: '₹88,000', professional: '₹2,20,000', enterprise: 'Custom' },
      route: '/ai-chatbot-automation'
    },
    {
      id: '2',
      title: 'Enterprise Workflow Ops',
      description: 'Build sophisticated automation workflows that connect your entire tech stack (HubSpot, Slack, Shopify) to eliminate manual work.',
      icon: 'Workflow',
      category: 'workflow',
      features: ['Visual Builder', 'Custom Webhooks', 'Error Handling', 'Database Sync', 'Scheduled Jobs'],
      benefits: ['Save 40+ hours/week', 'Zero Data Entry Errors', 'Scales infinitely'],
      useCases: ['Invoice Processing', 'Inventory Sync', 'Client Onboarding'],
      tools: [
        { name: 'n8n', logo: 'https://upload.wikimedia.org/wikipedia/commons/f/f6/N8n-logo.png', alt: 'n8n' },
        { name: 'Make', logo: "https://images.g2crowd.com/uploads/product/image/social_landscape/social_landscape_4b71a5a8acbdc0683a48e8955d5b7964/make-com.png", alt: 'Make' }
      ],
      pricing: { starter: '₹70,000', professional: '₹1,76,000', enterprise: 'Custom' },
      route: '/n8n-make-com-workflows'
    },
    {
      id: '3',
      title: 'WhatsApp Revenue Engine',
      description: 'Turn WhatsApp into a sales channel. Automate abandoned carts, appointment reminders, and lead nurturing sequences.',
      icon: 'MessageCircle',
      category: 'communication',
      features: ['Official API Access', 'Bulk Broadcasting', 'Interactive Buttons', 'CRM Retargeting'],
      benefits: ['98% Open Rates', 'Instant Engagement', 'Higher Conversion'],
      useCases: ['Abandoned Carts', 'Event Reminders', 'Newsletter Broadcasts'],
      tools: [
        { name: 'WhatsApp', logo: "https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg", alt: 'WhatsApp' },
        { name: 'HubSpot', logo: 'https://upload.wikimedia.org/wikipedia/commons/1/15/HubSpot_Logo.svg', alt: 'HubSpot' }
      ],
      pricing: { starter: '₹61,500', professional: '₹1,58,000', enterprise: 'Custom' },
      route: '/whats-app-email-automation'
    },
    {
      id: '4',
      title: 'Custom API Integrations',
      description: 'Need to connect a legacy system? We build custom Python/Node.js middleware to bridge gaps that standard tools can\'t.',
      icon: 'Code',
      category: 'workflow',
      features: ['REST/GraphQL', 'Secure Authentication', 'Rate Limiting', 'Real-time Sync'],
      benefits: ['Connect Anything', 'Secure Data Transfer', 'Custom Logic'],
      useCases: ['Legacy ERP Sync', 'Custom Dashboards', 'Payment Gateways'],
      tools: [
        { name: 'Python', logo: "https://upload.wikimedia.org/wikipedia/commons/c/c3/Python-logo-notext.svg", alt: 'Python' },
        { name: 'AWS', logo: "https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg", alt: 'AWS' }
      ],
      pricing: { starter: '₹1,32,000', professional: '₹3,52,000', enterprise: 'Custom' },
      route: '/custom-api-integrations'
    },
    {
      id: '5',
      title: 'SEO Services',
      description: 'Rank higher on Google and turn organic search into your most reliable source of new business.',
      icon: 'Search',
      category: 'digital-marketing',
      features: ['Technical SEO Audits', 'Keyword Strategy', 'On-Page Optimization', 'Link Building', 'Monthly Reporting'],
      benefits: ['Compounding organic traffic', 'Lower cost per lead over time', 'Visibility where buyers search'],
      useCases: ['Lead Generation', 'E-commerce Sales', 'Local Visibility'],
      tools: [
        { name: 'Google', logo: 'https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg', alt: 'Google' },
        { name: 'HubSpot', logo: 'https://upload.wikimedia.org/wikipedia/commons/1/15/HubSpot_Logo.svg', alt: 'HubSpot' }
      ],
      pricing: { starter: 'Custom', professional: 'Custom', enterprise: 'Custom' },
      route: '/seo-services'
    },
    {
      id: '6',
      title: 'Local SEO',
      description: 'Own the map pack and "near me" searches so nearby customers find you first.',
      icon: 'MapPin',
      category: 'digital-marketing',
      features: ['Google Business Profile', 'Local Citations', 'Review Management', 'Local Landing Pages', 'Map Pack Tracking'],
      benefits: ['More calls and visits', 'Stronger local reputation', 'Beat nearby competitors'],
      useCases: ['Local Services', 'Clinics & Salons', 'Restaurants & Retail'],
      tools: [
        { name: 'Google', logo: 'https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg', alt: 'Google' },
        { name: 'WhatsApp', logo: 'https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg', alt: 'WhatsApp' }
      ],
      pricing: { starter: 'Custom', professional: 'Custom', enterprise: 'Custom' },
      route: '/local-seo'
    },
    {
      id: '7',
      title: 'Social Media Marketing',
      description: 'Content, community, and campaigns that turn followers into enquiries and customers.',
      icon: 'Share2',
      category: 'digital-marketing',
      features: ['Content Calendars', 'Reels & Creatives', 'Community Management', 'Paid Social Boosts', 'Monthly Analytics'],
      benefits: ['Consistent brand presence', 'Engaged local audience', 'More inbound enquiries'],
      useCases: ['Brand Building', 'Product Launches', 'Event Promotion'],
      tools: [
        { name: 'Instagram', logo: 'https://upload.wikimedia.org/wikipedia/commons/a/a5/Instagram_icon.png', alt: 'Instagram' },
        { name: 'Facebook', logo: 'https://upload.wikimedia.org/wikipedia/commons/5/51/Facebook_f_logo_%282019%29.svg', alt: 'Facebook' }
      ],
      pricing: { starter: 'Custom', professional: 'Custom', enterprise: 'Custom' },
      route: '/social-media-marketing'
    },
    {
      id: '8',
      title: 'Google Ads & PPC',
      description: 'Search and shopping ads that put you at the top of Google from day one, with every rupee tracked.',
      icon: 'MousePointerClick',
      category: 'digital-marketing',
      features: ['Search Campaigns', 'Shopping & Performance Max', 'Landing Page CRO', 'Conversion Tracking', 'Weekly Optimization'],
      benefits: ['Instant visibility', 'Pay only for clicks', 'Measurable ROI'],
      useCases: ['High-Intent Leads', 'E-commerce Sales', 'Seasonal Offers'],
      tools: [
        { name: 'Google', logo: 'https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg', alt: 'Google' },
        { name: 'YouTube', logo: 'https://upload.wikimedia.org/wikipedia/commons/e/ef/Youtube_logo.png', alt: 'YouTube' }
      ],
      pricing: { starter: 'Custom', professional: 'Custom', enterprise: 'Custom' },
      route: '/google-ads-ppc'
    },
    {
      id: '9',
      title: 'Meta Ads',
      description: 'Facebook and Instagram ads engineered for leads, sales, and retargeting, not just likes.',
      icon: 'Megaphone',
      category: 'digital-marketing',
      features: ['Audience Targeting', 'Creative Testing', 'Retargeting Funnels', 'Lead Forms & CAPI', 'ROAS Reporting'],
      benefits: ['Reach buyers at scale', 'Lower cost per lead', 'Full-funnel tracking'],
      useCases: ['Lead Generation', 'E-commerce Sales', 'App Installs'],
      tools: [
        { name: 'Meta', logo: 'https://upload.wikimedia.org/wikipedia/commons/7/7b/Meta_Platforms_Inc._logo.svg', alt: 'Meta' },
        { name: 'Instagram', logo: 'https://upload.wikimedia.org/wikipedia/commons/a/a5/Instagram_icon.png', alt: 'Instagram' }
      ],
      pricing: { starter: 'Custom', professional: 'Custom', enterprise: 'Custom' },
      route: '/meta-ads'
    },
    {
      id: '10',
      title: 'Content Marketing',
      description: 'Blogs, videos, and guides that answer buyer questions, build trust, and keep ranking for years.',
      icon: 'PenLine',
      category: 'digital-marketing',
      features: ['SEO Blog Writing', 'Video Scripts', 'Lead Magnets', 'Content Calendars', 'Distribution'],
      benefits: ['Authority in your niche', 'Content that keeps working', 'Better sales conversations'],
      useCases: ['Thought Leadership', 'SEO Growth', 'Nurture Sequences'],
      tools: [
        { name: 'YouTube', logo: 'https://upload.wikimedia.org/wikipedia/commons/e/ef/Youtube_logo.png', alt: 'YouTube' },
        { name: 'LinkedIn', logo: 'https://upload.wikimedia.org/wikipedia/commons/c/ca/LinkedIn_logo_initials.png', alt: 'LinkedIn' }
      ],
      pricing: { starter: 'Custom', professional: 'Custom', enterprise: 'Custom' },
      route: '/content-marketing'
    },
    {
      id: '11',
      title: 'Email Marketing',
      description: 'Campaigns and automations that turn your list into repeat revenue, without spamming anyone.',
      icon: 'Mail',
      category: 'digital-marketing',
      features: ['Newsletter Campaigns', 'Drip Sequences', 'List Segmentation', 'Template Design', 'Deliverability'],
      benefits: ['Repeat purchases', 'Owned audience, no algorithm', 'Automated follow-ups'],
      useCases: ['Abandoned Carts', 'Product Launches', 'Customer Retention'],
      tools: [
        { name: 'HubSpot', logo: 'https://upload.wikimedia.org/wikipedia/commons/1/15/HubSpot_Logo.svg', alt: 'HubSpot' },
        { name: 'Google', logo: 'https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg', alt: 'Google' }
      ],
      pricing: { starter: 'Custom', professional: 'Custom', enterprise: 'Custom' },
      route: '/email-marketing'
    },
    {
      id: '12',
      title: 'Analytics & CRO',
      description: 'Dashboards, experiments, and fixes that turn more of your existing traffic into customers.',
      icon: 'BarChart3',
      category: 'digital-marketing',
      features: ['GA4 & Tag Setup', 'Conversion Audits', 'A/B Testing', 'Funnel Reports', 'Monthly Insights'],
      benefits: ['Decisions from data, not guesses', 'More conversions, same traffic', 'Clear marketing ROI'],
      useCases: ['Landing Page Lifts', 'Funnel Leaks', 'Ad Spend Efficiency'],
      tools: [
        { name: 'Google', logo: 'https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg', alt: 'Google' },
        { name: 'HubSpot', logo: 'https://upload.wikimedia.org/wikipedia/commons/1/15/HubSpot_Logo.svg', alt: 'HubSpot' }
      ],
      pricing: { starter: 'Custom', professional: 'Custom', enterprise: 'Custom' },
      route: '/analytics-and-cro'
    },
  ];

  const comparisonFeatures: ComparisonFeature[] = [
    { feature: 'Workflow Active Steps', starter: 'Up to 1,000', professional: 'Up to 50,000', enterprise: 'Unlimited' },
    { feature: 'Data Update Frequency', starter: '15 Mins', professional: '1 Min', enterprise: 'Real-time' },
    { feature: 'AI Agent Capabilities', starter: false, professional: true, enterprise: true },
    { feature: 'Custom API Dev', starter: false, professional: 'Limited', enterprise: 'Unlimited' },
    { feature: 'Support SLA', starter: '48 Hours', professional: '12 Hours', enterprise: '1 Hour' },
    { feature: 'Dedicated Engineer', starter: false, professional: false, enterprise: true },
  ];

  const stats: Stat[] = [
    { label: 'Hours Saved Monthly', value: '50k+', icon: 'Clock', description: 'Across all clients' },
    { label: 'Active Workflows', value: '2,500+', icon: 'Zap', description: 'Running 24/7' },
    { label: 'Success Rate', value: '99.9%', icon: 'Activity', description: 'Uptime guarantee' },
    { label: 'Avg ROI', value: '300%', icon: 'TrendingUp', description: 'First 90 days' }
  ];

  const faqs: FAQ[] = [
    {
      question: 'Do you charge per workflow or per month?',
      answer: 'We operate on a monthly retainer basis which includes maintenance, hosting, and optimization. This ensures your automations never break.'
    },
    {
      question: 'Can you integrate with my specific industry software?',
      answer: 'Yes. If it has an API, we can connect to it. Even if it doesn\'t, we have browser automation tools to handle legacy systems.'
    },
    {
      question: 'Who owns the code/workflows?',
      answer: 'You do. We build it, but it belongs to your business. We don\'t believe in vendor lock-in.'
    }
  ];

  const filteredServices = activeCategory === 'all' 
    ? services 
    : services.filter((service) => service.category === activeCategory);

  return (
    <>
      <Helmet>
        <title>Our Services | Make Mind Automations</title>
        <meta name="description" content="Explore our done-for-you automation services, including Make.com workflows, n8n automations, WhatsApp & email sequences, AI chatbots, custom API integrations, and industry-specific systems. Serving businesses across India." />
        <meta name="keywords" content="Automation Services, AI Agency Pricing, n8n Experts, Make.com Consultants, Automation Agency India" />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
        <link
  rel="canonical"
  href="https://www.makemindautomations.tech/services-overview"
/>
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        <main>
          <HeroSection />

          <section id="catalog" className="py-20 bg-background relative z-10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <ServiceCategories
                categories={serviceCategories}
                activeCategory={activeCategory}
                onCategoryChange={setActiveCategory} 
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
                {filteredServices.map((service) => (
                  <ServiceCard key={service.id} service={service} />
                ))}
              </div>
            </div>
          </section>

          <ComparisonMatrix features={comparisonFeatures} />
          
          <StatsSection stats={stats} />
          
          
          <FAQSection faqs={faqs} />
          
          <CTASection />
        </main>

        <Footer />
      </div>
    </>
  );
};

export default ServicesOverview;